// (c) chaehongee. Confidential and proprietary.

export interface ImageSize {
    w: number
    h: number
}

const inFlightBySrc = new Map<string, Promise<ImageSize>>()
const resolvedBySrc = new Map<string, ImageSize>()

export interface MediaSizeRequest {
    src: string
    mediaType?: string
}

export async function preloadImageSizes(
    srcs: string[],
    timeoutMs: number = 8000
): Promise<Record<string, ImageSize>> {
    const uniqueSrcs = Array.from(new Set(srcs.filter(Boolean)))
    const entries = await Promise.all(
        uniqueSrcs.map(async (src) => {
            try {
                const size = await preloadImageSize(src, timeoutMs)
                return [src, size] as const
            } catch {
                return null
            }
        })
    )
    const out: Record<string, ImageSize> = {}
    for (const e of entries) {
        if (!e) continue
        out[e[0]] = e[1]
    }
    return out
}

export async function preloadMediaSizes(
    items: MediaSizeRequest[],
    timeoutMs: number = 8000
): Promise<Record<string, ImageSize>> {
    const uniqueItems = Array.from(
        new Map(
            items
                .filter((item) => item?.src)
                .map((item) => [item.src, item])
        ).values()
    )
    const entries = await Promise.all(
        uniqueItems.map(async (item) => {
            try {
                const size = await preloadMediaSize(item, timeoutMs)
                return [item.src, size] as const
            } catch {
                return null
            }
        })
    )
    const out: Record<string, ImageSize> = {}
    for (const e of entries) {
        if (!e) continue
        out[e[0]] = e[1]
    }
    return out
}

export function preloadMediaSize(
    item: MediaSizeRequest,
    timeoutMs: number = 8000
): Promise<ImageSize> {
    const mediaType = (item.mediaType || 'image').toLowerCase()
    if (mediaType === 'video') {
        return preloadVideoSize(item.src, timeoutMs)
    }
    return preloadImageSize(item.src, timeoutMs)
}

export function preloadImageSize(
    src: string,
    timeoutMs: number = 8000
): Promise<ImageSize> {
    const cached = resolvedBySrc.get(src)
    if (cached) return Promise.resolve(cached)

    const existing = inFlightBySrc.get(src)
    if (existing) return existing

    const p = new Promise<ImageSize>((resolve, reject) => {
        const img = new Image()
        img.referrerPolicy = 'no-referrer'

        let done = false
        const finishOk = () => {
            if (done) return
            done = true
            const size = { w: img.naturalWidth || 0, h: img.naturalHeight || 0 }
            if (size.w > 0 && size.h > 0) {
                resolvedBySrc.set(src, size)
                resolve(size)
            } else {
                reject(new Error('Image loaded but natural size is empty'))
            }
        }
        const finishErr = (err: unknown) => {
            if (done) return
            done = true
            reject(err instanceof Error ? err : new Error('Image preload failed'))
        }

        img.onload = () => finishOk()
        img.onerror = () => finishErr(new Error('Image preload error'))
        img.onabort = () => finishErr(new Error('Image preload aborted'))

        // Start request
        img.src = src

        // Handle cached-complete images where onload might not fire reliably.
        if (img.complete) {
            queueMicrotask(() => finishOk())
        }

        if (timeoutMs > 0) {
            window.setTimeout(() => {
                finishErr(new Error('Image preload timeout'))
            }, timeoutMs)
        }
    }).finally(() => {
        inFlightBySrc.delete(src)
    })

    inFlightBySrc.set(src, p)
    return p
}

export function preloadVideoSize(
    src: string,
    timeoutMs: number = 8000
): Promise<ImageSize> {
    const cached = resolvedBySrc.get(src)
    if (cached) return Promise.resolve(cached)

    const existing = inFlightBySrc.get(src)
    if (existing) return existing

    const p = new Promise<ImageSize>((resolve, reject) => {
        const video = document.createElement('video')
        video.preload = 'metadata'
        video.muted = true
        video.playsInline = true

        let done = false
        const finishOk = () => {
            if (done) return
            done = true
            const size = {
                w: video.videoWidth || 0,
                h: video.videoHeight || 0,
            }
            if (size.w > 0 && size.h > 0) {
                resolvedBySrc.set(src, size)
                resolve(size)
            } else {
                reject(new Error('Video loaded but metadata size is empty'))
            }
        }
        const finishErr = (err: unknown) => {
            if (done) return
            done = true
            reject(err instanceof Error ? err : new Error('Video preload failed'))
        }

        video.onloadedmetadata = () => finishOk()
        video.onerror = () => finishErr(new Error('Video preload error'))
        video.onabort = () => finishErr(new Error('Video preload aborted'))

        // Start request
        video.src = src
        video.load()

        if (timeoutMs > 0) {
            window.setTimeout(() => {
                finishErr(new Error('Video preload timeout'))
            }, timeoutMs)
        }
    }).finally(() => {
        inFlightBySrc.delete(src)
    })

    inFlightBySrc.set(src, p)
    return p
}


